import React from 'react';
import { motion } from 'framer-motion';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex min-h-[42px] items-center justify-center gap-2 whitespace-nowrap rounded-xl font-medium tracking-[0.01em] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none';
  
  const variants = {
    primary: 'bg-blue-600 text-white shadow-sm shadow-blue-900/20 hover:bg-blue-700 hover:shadow-md focus:ring-blue-500',
    secondary: 'bg-gray-200 text-gray-900 shadow-sm hover:bg-gray-300 hover:shadow-md focus:ring-gray-500 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600',
    success: 'bg-emerald-600 text-white shadow-sm shadow-emerald-900/20 hover:bg-emerald-700 hover:shadow-md focus:ring-emerald-500',
    danger: 'bg-red-600 text-white shadow-sm shadow-red-900/20 hover:bg-red-700 hover:shadow-md focus:ring-red-500',
    warning: 'bg-amber-600 text-white shadow-sm shadow-amber-900/20 hover:bg-amber-700 hover:shadow-md focus:ring-amber-500',
    outline: 'border border-blue-200 bg-white text-blue-600 shadow-sm hover:border-blue-500 hover:bg-blue-600 hover:text-white focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-blue-300 dark:hover:border-blue-500 dark:hover:bg-blue-600 dark:hover:text-white',
    ghost: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:ring-gray-500 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
    gradient: 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm shadow-blue-900/20 hover:from-blue-700 hover:to-purple-700 hover:shadow-md focus:ring-blue-500'
  };
  
  const sizes = {
    sm: 'min-h-[38px] px-3.5 py-2 text-sm',
    md: 'px-4 py-2.5 text-sm sm:text-base',
    lg: 'min-h-[48px] px-5 py-3 text-base sm:text-lg',
    xl: 'min-h-[54px] px-6 py-3.5 text-lg sm:px-8 sm:text-xl'
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`;
  
  const buttonContent = (
    <>
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </>
  );
  
  return (
    <motion.button
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      {...props}
    >
      {buttonContent}
    </motion.button>
  );
};

export default Button;
export { Button };
